import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';
import { Product, ProductCellBalance, Warehouse } from 'src/typings';
import { NewCatalogNextFifoLot, NewCatalogService } from '../../new-catalog/new-catalog.service';
import { ProcurementAnalyticsService } from '../../procurement/analytics/procurement-analytics.service';
import { AnalyticsWarehouseMinimumStock } from '../../procurement/analytics/procurement-analytics.types';
import { ProductMinimumStockChange } from '../../procurement/analytics/procurement-minimum-stock.component';
import { CatalogStockDialogComponent } from '../catalog-stock-dialog/catalog-stock-dialog.component';

interface WarehouseStockRow {
  warehouseId: number;
  warehouseName: string;
  warehouse?: Warehouse;
  quantity: number;
  reservedQuantity: number | null;
  freeQuantity: number | null;
  customerReservedQuantity: number | null;
  transferReservedQuantity: number | null;
  supplierReturnReservedQuantity: number | null;
  fallbackQuantity: number;
  minimumStock: number | null;
  draft: number | null;
  saving: boolean;
  saved: boolean;
  error: string;
  cells: ProductCellBalance[];
  legacyCell: string;
  lots: NewCatalogNextFifoLot[];
}

@Component({
  selector: 'app-product-warehouse-table',
  templateUrl: './product-warehouse-table.component.html',
  styleUrls: ['./product-warehouse-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductWarehouseTableComponent implements OnChanges, OnDestroy {
  @Input() product!: Product;
  @Input() minimumStockChanges: Record<number, AnalyticsWarehouseMinimumStock> = {};
  @Output() minimumStockSaved = new EventEmitter<ProductMinimumStockChange>();

  rows: WarehouseStockRow[] = [];
  minimumLoading = false;
  minimumError = '';
  lotsLoading = false;
  lotsError = '';

  private productId = 0;
  private stockVersion = 0;
  private minimumLoaded = false;
  private minimumRefreshPending = false;
  private destroyed = false;
  private requests = new Subscription();
  private minimumRequest?: Subscription;
  private lotsRequest?: Subscription;

  constructor(
    private readonly analytics: ProcurementAnalyticsService,
    private readonly catalog: NewCatalogService,
    private readonly dialog: MatDialog,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product && this.product?.id) {
      const changedProduct = this.productId !== this.product.id;
      if (changedProduct) {
        this.requests.unsubscribe();
        this.requests = new Subscription();
        this.rows = [];
        this.productId = this.product.id;
        this.minimumLoading = false;
        this.minimumLoaded = false;
        this.minimumRefreshPending = false;
        this.minimumError = '';
        this.lotsLoading = false;
        this.lotsError = '';
      }
      this.stockVersion++;
      const addedWarehouse = this.syncStocks(true);
      if (changedProduct) this.loadMinimums();
      else if (addedWarehouse) this.refreshNewWarehouseMinimums();
      // A scoped product update can change batch costs even if total stock is unchanged.
      this.loadLots();
    }
    if (changes.minimumStockChanges) this.applySavedMinimums();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requests.unsubscribe();
  }

  private rowFor(warehouseId: number, warehouseName: string): WarehouseStockRow {
    let row = this.rows.find((item) => item.warehouseId === warehouseId);
    if (!row) {
      row = {
        warehouseId, warehouseName, quantity: 0, fallbackQuantity: 0, minimumStock: null, draft: null,
        reservedQuantity: null, freeQuantity: null, customerReservedQuantity: null,
        transferReservedQuantity: null, supplierReturnReservedQuantity: null,
        saving: false, saved: false, error: '', cells: [], legacyCell: '', lots: []
      };
      this.rows.push(row);
    }
    return row;
  }

  private syncStocks(clearFallback = false): boolean {
    const previousCount = this.rows.length;
    for (const stock of this.product.stock || []) {
      const row = this.rowFor(Number(stock.warehouse.id), stock.warehouse.description);
      row.warehouse = stock.warehouse;
      row.warehouseName = stock.warehouse.description;
    }
    for (const row of this.rows) {
      const stock = (this.product.stock || []).find(
        (item) => Number(item.warehouse.id) === row.warehouseId
      );
      if (clearFallback) row.fallbackQuantity = 0;
      row.quantity = stock ? Number(stock.quan || 0) : row.fallbackQuantity;
      row.reservedQuantity = this.availabilityQuantity(stock?.reservedQuantity);
      row.freeQuantity = this.availabilityQuantity(stock?.freeQuantity);
      row.customerReservedQuantity = this.availabilityQuantity(stock?.customerReservedQuantity);
      row.transferReservedQuantity = this.availabilityQuantity(stock?.transferReservedQuantity);
      row.supplierReturnReservedQuantity = this.availabilityQuantity(stock?.supplierReturnReservedQuantity);
      row.legacyCell = stock?.cell || '';
      row.cells = (this.product.cellBalances || []).filter(
        (balance) => Number(balance.warehouseId) === row.warehouseId
      );
    }
    return this.rows.length !== previousCount;
  }

  private availabilityQuantity(value: number | undefined): number | null {
    // A warehouse found only in minimums/lots has no reservation snapshot.
    if (value == null) return null;
    const quantity = Number(value);
    return Number.isFinite(quantity) ? quantity : null;
  }

  private refreshNewWarehouseMinimums(): void {
    if (this.minimumLoading) this.minimumRefreshPending = true;
    else if (this.minimumLoaded) this.loadMinimums();
  }

  loadMinimums(): void {
    if (this.destroyed || !this.productId || this.minimumLoading) return;
    this.minimumRequest?.unsubscribe();
    this.minimumLoading = true;
    this.minimumRefreshPending = false;
    this.minimumError = '';
    const stockVersion = this.stockVersion;
    this.minimumRequest = this.analytics.minimumStocks(this.productId).subscribe({
      next: (stocks) => {
        for (const stock of stocks) {
          const row = this.rowFor(stock.warehouseId, stock.warehouseName);
          this.applyMinimum(row, stock.minimumStock);
          if (this.stockVersion === stockVersion) row.fallbackQuantity = Number(stock.quantity || 0);
        }
        this.syncStocks();
        this.applySavedMinimums();
        this.minimumLoading = false;
        this.minimumLoaded = true;
        if (this.minimumRefreshPending) {
          this.minimumRefreshPending = false;
          this.loadMinimums();
        }
        this.cd.markForCheck();
      },
      error: () => {
        this.minimumLoading = false;
        this.minimumError = 'Не удалось загрузить минимальные остатки.';
        this.cd.markForCheck();
      }
    });
    this.requests.add(this.minimumRequest);
  }

  loadLots(): void {
    if (this.destroyed || !this.productId) return;
    this.lotsRequest?.unsubscribe();
    this.lotsLoading = true;
    this.lotsError = '';
    this.lotsRequest = this.catalog.getStockLots(this.productId).subscribe({
      next: (response) => {
        const previousCount = this.rows.length;
        for (const row of this.rows) row.lots = [];
        for (const lot of response.items) {
          if (lot.remainingQty > 0) {
            this.rowFor(lot.warehouseId, lot.warehouseName).lots.push(lot);
          }
        }
        this.syncStocks();
        if (this.rows.length !== previousCount) this.refreshNewWarehouseMinimums();
        this.lotsLoading = false;
        this.cd.markForCheck();
      },
      error: () => {
        this.lotsLoading = false;
        this.lotsError = 'Не удалось загрузить закупочные цены партий.';
        this.cd.markForCheck();
      }
    });
    this.requests.add(this.lotsRequest);
  }

  private applyMinimum(row: WarehouseStockRow, value: number): void {
    if (row.saving) return;
    if (row.minimumStock === null || row.draft === row.minimumStock) row.draft = value;
    row.minimumStock = value;
  }

  private applySavedMinimums(): void {
    for (const row of this.rows) {
      const saved = this.minimumStockChanges[row.warehouseId];
      if (saved) this.applyMinimum(row, saved.minimumStock);
    }
  }

  validDraft(row: WarehouseStockRow): boolean {
    return row.draft !== null && Number.isInteger(row.draft) &&
      row.draft >= 0 && row.draft <= 1000000;
  }

  changeDraft(row: WarehouseStockRow, value: number | null): void {
    if (row.saving) return;
    row.draft = value;
    row.saved = false;
    row.error = '';
  }

  save(row: WarehouseStockRow): void {
    if (this.destroyed || !this.rows.includes(row) || row.saving || row.minimumStock === null ||
      !this.validDraft(row) || row.draft === row.minimumStock) return;
    const productId = this.productId;
    const stockVersion = this.stockVersion;
    const submitted = row.draft!;
    row.saving = true;
    row.saved = false;
    row.error = '';
    this.requests.add(this.catalog.updateMinimumStock({
      product_id: productId, warehouse_id: row.warehouseId, minimum_stock: submitted
    }).subscribe({
      next: (saved) => {
        row.minimumStock = saved.minimumStock;
        if (row.draft === submitted) row.draft = saved.minimumStock;
        if (this.stockVersion === stockVersion) {
          row.fallbackQuantity = saved.quantity;
          // Keep physical stock and availability from the same product snapshot.
          // The minimum-save response has no reservation quantities.
          if (!(this.product.stock || []).some((stock) => Number(stock.warehouse.id) === row.warehouseId)) {
            row.quantity = saved.quantity;
          }
        }
        row.saving = false;
        row.saved = true;
        this.minimumStockSaved.emit({ productId, stock: saved });
        this.cd.markForCheck();
      },
      error: () => {
        row.saving = false;
        row.error = 'Не удалось сохранить. Повторите попытку.';
        this.cd.markForCheck();
      }
    }));
  }

  editStock(row: WarehouseStockRow): void {
    if (!row.warehouse) return;
    const productId = this.productId;
    const ref = this.dialog.open(CatalogStockDialogComponent, {
      width: '720px', maxWidth: '95vw', autoFocus: false, restoreFocus: true
    });
    const editor = ref.componentInstance;
    editor.product = this.product;
    editor.warehouse = row.warehouse;
    this.requests.add(ref.afterClosed().pipe(take(1)).subscribe((updated?: Product) => {
      if (this.productId !== productId) return;
      // The address dialog may refresh its product even when it closes via Escape.
      const fresh = updated || editor.product;
      if (fresh?.id === productId) {
        this.product = fresh;
        this.stockVersion++;
        const addedWarehouse = this.syncStocks(true);
        if (addedWarehouse) this.refreshNewWarehouseMinimums();
        this.loadLots();
        this.cd.markForCheck();
      }
    }));
  }

  trackWarehouse(_index: number, row: WarehouseStockRow): number {
    return row.warehouseId;
  }

  trackLot(_index: number, lot: NewCatalogNextFifoLot): number {
    return lot.lotId;
  }
}
